import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, CircularProgress } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactElement, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCanAccess, useDataProvider, useNotify } from 'react-admin';
import type { ActionLogsByUsersResource } from '../../../../Entities/ActionLogsByUser';
import { fetchOrCreateReportWithActionLogsByUsers, type ActionLogsByUserFilter } from './api';

interface PreviewButtonProps {
  onPreviewStart: () => void;
  onPreviewSuccess: (records: ActionLogsByUsersResource[]) => void;
  onPreviewError: () => void;
  autoPreviewOnMount?: boolean;
}

export default function PreviewButton({
  onPreviewStart,
  onPreviewSuccess,
  onPreviewError,
  autoPreviewOnMount = false,
}: PreviewButtonProps): ReactElement | null {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const hasAutoPreviewTriggered = useRef(false);

  const reportsCreateAccess = useCanAccess({ resource: 'reports', action: 'create' });
  const actionLogsByUsersListAccess = useCanAccess({
    resource: 'action_logs_by_users',
    action: 'list',
  });

  const mutation = useMutation({
    mutationFn: (filter: ActionLogsByUserFilter) =>
      fetchOrCreateReportWithActionLogsByUsers(queryClient, dataProvider, filter),
    onSuccess: (result) => {
      onPreviewSuccess(result.data);
    },
  });

  const handlePreview = handleSubmit(async (values) => {
    if (mutation.isPending) {
      return;
    }

    onPreviewStart();

    try {
      await mutation.mutateAsync(values as ActionLogsByUserFilter);
    } catch (error) {
      onPreviewError();
      notify(error instanceof Error ? error.message : 'Preview failed', { type: 'error' });
    }
  });

  useEffect(() => {
    if (
      !autoPreviewOnMount ||
      hasAutoPreviewTriggered.current ||
      !reportsCreateAccess.canAccess ||
      !actionLogsByUsersListAccess.canAccess
    ) {
      return;
    }

    hasAutoPreviewTriggered.current = true;
    void handlePreview();
  }, [
    actionLogsByUsersListAccess.canAccess,
    autoPreviewOnMount,
    handlePreview,
    reportsCreateAccess.canAccess,
  ]);

  if (!reportsCreateAccess.canAccess || !actionLogsByUsersListAccess.canAccess) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      onClick={() => void handlePreview()}
      disabled={mutation.isPending}
      startIcon={
        mutation.isPending ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="small" />
      }
    >
      {mutation.isPending ? 'Loading...' : 'Preview'}
    </Button>
  );
}
